import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag, Badge,
  FormControl, FormLabel,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import {
  ImmCaso, listarCasos, bajaCaso,
} from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge, NIVEL_RIESGO_COLOR } from '../_shared';
import FormularioCaso from './FormularioCaso';

const NIVELES_RIESGO = ['Bajo', 'Medio', 'Alto'];
const ESTATUS = ['Abierto', 'EnSeguimiento', 'Cerrado', 'Anulado'];

const TablaCasos: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmCaso[]>([]);
  const [selected, setSelected] = useState<ImmCaso | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [motivoBaja, setMotivoBaja] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [filtroRiesgo, setFiltroRiesgo] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarCasos());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const folioExp = (item: ImmCaso) => item.expediente?.folio ?? ('#' + item.expedienteId);

  const datosFiltrados = data.filter((item) => {
    const matchRiesgo = filtroRiesgo ? item.nivelRiesgo === filtroRiesgo : true;
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? folioExp(item).toLowerCase().includes(q) ||
        item.tipoViolencia?.descripcion?.toLowerCase().includes(q)
      : true;
    return matchRiesgo && matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const abrirBaja = (item: ImmCaso) => { setSelected(item); setMotivoBaja(''); bajaD.onOpen(); };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await bajaCaso(selected.id, motivoBaja);
      toast({ title: 'Caso dado de baja', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo dar de baja.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Expediente': folioExp(item),
      'Fecha incidente': fmt(item.fechaIncidente),
      'Tipo de violencia': item.tipoViolencia?.descripcion || '',
      'Nivel de riesgo': item.nivelRiesgo,
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Casos IMM');
    XLSX.writeFile(wb, `Casos_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={2} fontStyle="italic">
        Sin borrado físico; los cambios quedan en el historial.
      </Text>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por expediente o tipo..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '260px' }} />
          <Select placeholder="Nivel de riesgo" value={filtroRiesgo} onChange={(e) => { setFiltroRiesgo(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {NIVELES_RIESGO.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="150px">
            {ESTATUS.map((v) => <option key={v} value={v}>{v}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nuevo caso</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Expediente</Th>
              <Th color="white" fontSize="xs">Fecha incidente</Th>
              <Th color="white" fontSize="xs">Tipo violencia</Th>
              <Th color="white" fontSize="xs">Riesgo</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.400">No se encontraron casos</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{folioExp(item)}</Tag></Td>
                  <Td>{fmt(item.fechaIncidente)}</Td>
                  <Td><Text noOfLines={1} fontSize="sm">{item.tipoViolencia?.descripcion || '—'}</Text></Td>
                  <Td><Badge colorScheme={NIVEL_RIESGO_COLOR[item.nivelRiesgo] || 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">{item.nivelRiesgo || '—'}</Badge></Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => abrirBaja(item)}>Baja</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver (con historial) */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Caso</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioCaso modo="view" data={selected} onCancelar={verD.onClose} />}</ModalBody>
          <ModalFooter><Button onClick={verD.onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar caso</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioCaso modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nuevo caso</ModalHeader>
          <ModalBody py={5}><FormularioCaso modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Baja lógica con motivo */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Dar de baja caso</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3}>
              El caso del expediente <strong>{selected ? folioExp(selected) : ''}</strong> se dará de baja (sin borrado físico). El movimiento quedará en el historial.
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Motivo de la baja</FormLabel>
              <Input value={motivoBaja} onChange={(e) => setMotivoBaja(e.target.value)} placeholder="Describe el motivo..." />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Dar de baja</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaCasos;
